import { NSARouterMakerResult } from 'namirasoft-account-react';
import { EncodingOperation } from 'namirasoft-core';
import { UserFullRow } from 'namirasoft-password';
import { EncryptionOperation, NSBox, NSBoxCombo, NSBoxSensitive, NSButtonBlue, NSColumn, NSLabelErrorNotifier, NSPanel, NSRow } from 'namirasoft-site-react';
import { Component, createRef, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { PasswordLayout } from '../PasswordLayout';
import { Storage } from '../Storage';

const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = MS_PER_MINUTE * 60;
const MS_PER_DAY = MS_PER_HOUR * 24;
const MS_PER_MONTH = MS_PER_DAY * 30;
const MS_PER_YEAR = MS_PER_MONTH * 12;
const MS_PERMANENT = MS_PER_YEAR * 100;

const RETENTION_OPTIONS = [
    { title: "5 Minutes", value: String(MS_PER_MINUTE * 5) },
    { title: "15 Minutes", value: String(MS_PER_MINUTE * 15) },
    { title: "1 Hour", value: String(MS_PER_HOUR) },
    { title: "1 Day", value: String(MS_PER_DAY) },
    { title: "1 Month", value: String(MS_PER_MONTH) },
    { title: "Permanently", value: String(MS_PERMANENT) },
];

const DEFAULT_RETENTION = MS_PER_DAY;

export interface DecryptMasterKeyLayoutProps extends NSARouterMakerResult
{
    user: UserFullRow;
    children?: ReactNode;
}

export interface DecryptMasterKeyLayoutState
{
    is_unlocked: boolean;
    is_password_error_visible: boolean;
}

export class DecryptMasterKeyLayout extends Component<DecryptMasterKeyLayoutProps, DecryptMasterKeyLayoutState>
{
    private NSBoxSensitive_Password = createRef<NSBoxSensitive>();
    private NSBoxCombo_RetentionDuration = createRef<NSBoxCombo>();

    constructor(props: DecryptMasterKeyLayoutProps)
    {
        super(props);
        this.state = {
            is_unlocked: Storage.hasMasterKey(props.user.id),
            is_password_error_visible: false,
        }
        this.cacheMasterKey = this.cacheMasterKey.bind(this);
        this.togglePasswordError = this.togglePasswordError.bind(this);
    }

    async cacheMasterKey(onFinished: () => void)
    {
        try
        {
            let password = this.NSBoxSensitive_Password.current?.getValue() ?? "";
            let encoded_password = EncodingOperation.Base64Encode(password);
            let master_key: string;
            try
            {
                master_key = await EncryptionOperation.AES256GCMDecrypt(encoded_password, this.props.user.cipher);
            }
            catch
            {
                this.togglePasswordError(true);
                onFinished();
                return;
            }

            let selected_retention = Number(this.NSBoxCombo_RetentionDuration.current?.getValueOne() ?? DEFAULT_RETENTION);
            let retention_duration = Number.isFinite(selected_retention) && selected_retention > 0
                ? selected_retention
                : DEFAULT_RETENTION;

            await Storage.setMasterKey(this.props.user.id, master_key, retention_duration);
            Storage.setDefaultRetentionDuration(retention_duration);

            this.setState(() => ({ is_unlocked: true }), onFinished);
        }
        catch (error)
        {
            this.props.notifier.onError(error as Error);
            onFinished();
        }
    }

    togglePasswordError(show: boolean)
    {
        this.setState(() => ({ is_password_error_visible: show }));
    }

    override render()
    {
        if (this.state.is_unlocked)
            return <>{this.props.children}</>;

        let stored_retention = Storage.getDefaultRetentionDuration();
        let default_retention = String(
            RETENTION_OPTIONS.some(o => o.value === String(stored_retention)) ? stored_retention : DEFAULT_RETENTION
        );

        return (
            <PasswordLayout
                {...this.props}
                action={{
                    title: "Unlock",
                    description: "Enter your master password to unlock your vault and view its content."
                }}
            >
                <NSPanel classList={["mx-auto"]}>
                    <NSColumn style={{ alignItems: "center" }}>
                        <NSBoxSensitive
                            {...this.props}
                            ref={this.NSBoxSensitive_Password}
                            title="Master Password"
                            auto_complete='new-password'
                            required
                        />
                        <NSBoxCombo
                            ref={this.NSBoxCombo_RetentionDuration}
                            title="Retention Duration"
                            required
                            multiple={false}
                            clearable={false}
                            defaultValue={default_retention}
                            getOptions={() => RETENTION_OPTIONS}
                        />
                        {
                            this.state.is_password_error_visible &&
                            <NSRow style={{ width: NSBox.width.one, justifyContent: "flex-start" }}>
                                <NSLabelErrorNotifier
                                    clearError={() => this.togglePasswordError(false)}
                                    error="Master password is incorrect."
                                />
                            </NSRow>
                        }
                        <NSButtonBlue
                            title="Apply"
                            onClick={{
                                showLoading: true,
                                action: this.cacheMasterKey,
                            }}
                        />
                        <Link to="/password/change">Change Master Password</Link>
                        <Link to="/password/recovery">Forgot Master Password</Link>
                    </NSColumn>
                </NSPanel>
            </PasswordLayout>
        )
    }
}

export default DecryptMasterKeyLayout;